import React, {useEffect, useReducer, useRef, useState} from "react";
import {Observer} from "mobx-react";
import styled from "styled-components";
import {useAppStore} from "../../../../StateManager";
import {SearchFieldV5} from "../../input/search/SearchFieldV5";
import type {Portfolio} from "../../../../data/models/Portfolio";
import {XyiconFeature} from "../../../../generated/api/base";
import {StringUtils} from "../../../../utils/data/string/StringUtils";
import {colorPalette} from "../../styles/colorPalette";
import {baseDistance, ELLIPSIS, fontSize, fontWeight, radius} from "../../styles/styles";
import {SearchFieldStyled} from "../../input/search/SearchField.styled";
import InfoIcon from "../../icons/circle-information.svg?react";
import {createFilterState, filterModels} from "../../../../data/models/filter/Filter";
import {SortDirection} from "../../../../data/models/ViewUtils";
import type {FilterOperator} from "../../../../data/models/filter/operator/FilterOperator";
import type {IModel} from "../../../../data/models/Model";
import {ReactUtils} from "../../../utils/ReactUtils";
import {openWorkspaceFromPortfolioModule} from "../ActionBarUtils";
import {Debouncer} from "../../../../utils/function/Debouncer";
import type {IPortfolioWidgetData} from "./HomeDashboard";

interface IPortfolioListProps {
	readonly widgetData: IPortfolioWidgetData;
}

export const PortfolioList = (props: IPortfolioListProps) => {
	const {widgetData} = props;
	const appState = useAppStore((state) => state.appState);
	const [searchString, setSearchString] = useState<string>("");
	const [, forceUpdate] = useReducer((x) => x + 1, 0);
	const authDataUpdatedSignal = appState.app.transport.services.auth.signals.authDataUpdated;
	const debouncer = useRef<Debouncer>(new Debouncer(500));

	useEffect(() => {
		authDataUpdatedSignal.add(forceUpdate);

		return () => {
			authDataUpdatedSignal.remove(forceUpdate);
		};
	}, [authDataUpdatedSignal]);

	const onPortfolioClick = (portfolioId: string) => {
		// In case of a "double-click" (one click after another), we could end up resetting the spaces state and redirecting to Spaces instead of opening the last active view.
		// Debouncing prevents multiple calls conflicting each other (on a slow network) and causing unexpected behavior.
		debouncer.current.debounce(() => {
			openWorkspaceFromPortfolioModule(appState, portfolioId);
		});
	};

	const onDetailsClick = (event: React.MouseEvent, portfolio: Portfolio) => {
		event.stopPropagation();

		appState.app.onDetailsClick(portfolio, true, false);
	};

	const createFilters = () => {
		const {conditions, operator} = widgetData.filter;
		const filters = createFilterState();
		filters.type = "advanced";
		if (operator !== "*") {
			// we can be sure that every fieldRefId is an actual field
			conditions.forEach((condition, index: number) => {
				filters.filters.advanced.push({
					type: "filter",
					value: {
						field: condition.fieldRefId,
						operator: condition.operator,
						param: condition.value,
					},
				});

				if (conditions[index + 1]) {
					filters.filters.advanced.push({type: "separator", value: operator});
				}
			});
		}

		return filters;
	};

	return (
		<Observer>
			{() => {
				const portfolios = appState.actions.getList(XyiconFeature.Portfolio);
				let filteredSortedPortfolios: IModel[] = portfolios;

				if (
					widgetData.filter.operator !== "*" &&
					widgetData.filter.conditions.some(
						(condition: {fieldRefId: string; operator: FilterOperator; value: string}) => !appState.actions.getFieldByRefId(condition.fieldRefId),
					)
				) {
					filteredSortedPortfolios = [];
				} else {
					const filters = createFilters();
					const filteredByWidgetData = filterModels(portfolios, filters, appState);
					filteredSortedPortfolios = searchString
						? filteredByWidgetData.filter((p) => StringUtils.containsIgnoreCase(p.name as string, searchString))
						: filteredByWidgetData;

					if (widgetData.sortOrder && widgetData.sortOrder.order && widgetData.sortOrder.fieldName) {
						filteredSortedPortfolios = filteredSortedPortfolios.toSorted((p1, p2) => {
							const val1 = appState.actions.renderValue(p1, widgetData.sortOrder.fieldName) || "";
							const val2 = appState.actions.renderValue(p2, widgetData.sortOrder.fieldName) || "";
							return (
								StringUtils.sortIgnoreCase(val1, val2) *
								(SortDirection[widgetData.sortOrder.order.toUpperCase() as keyof typeof SortDirection] as unknown as number)
							);
						});
					}
				}

				return (
					<WidgetStyled>
						<TitleStyled $color={widgetData.title.color}>{widgetData.title.text}</TitleStyled>
						{filteredSortedPortfolios.length > 0 || searchString ? (
							<>
								<SearchFieldV5
									value={searchString}
									onInput={setSearchString}
									placeholder="Find Portfolio"
								/>
								<LabelStyled>Portfolio Name</LabelStyled>
								{filteredSortedPortfolios.length === 0 ? (
									<>No matching results found.</>
								) : (
									<WidgetListStyled className={ReactUtils.cls({empty: filteredSortedPortfolios.length === 0})}>
										{filteredSortedPortfolios.map((p) => {
											return (
												<WidgetRowStyled
													key={p.id}
													onClick={() => onPortfolioClick(p.id)}
												>
													<span title={p.name}>{p.name}</span>
													<InfoIcon
														className="info"
														onClick={(e) => onDetailsClick(e, p as Portfolio)}
													/>
												</WidgetRowStyled>
											);
										})}
									</WidgetListStyled>
								)}
							</>
						) : (
							<>There are no portfolios to display. Contact an admin if you need access.</>
						)}
					</WidgetStyled>
				);
			}}
		</Observer>
	);
};

export const TitleStyled = styled.div<{$color?: string}>`
	font-size: 24px;
	line-height: 32px;
	min-height: 32px;
	font-weight: ${fontWeight.bold};
	color: ${(props) => props.$color || colorPalette.gray.c950};
	${ELLIPSIS}
`;

export const WidgetStyled = styled.div`
	height: 100%;
	width: 100%;
	padding: ${baseDistance.md};
	display: flex;
	flex-direction: column;
	gap: ${baseDistance.md};

	${SearchFieldStyled} {
		width: 100%;
	}
`;

const LabelStyled = styled.div`
	font-size: ${fontSize.sm};
	line-height: 16px;
	color: ${colorPalette.gray.c700Dark};
	margin-left: ${baseDistance.sm};
`;

export const WidgetListStyled = styled.div`
	display: flex;
	flex-direction: column;
	overflow-y: auto;
	contain: layout paint style;

	&.empty {
		overflow: hidden;
	}
`;

export const WidgetRowStyled = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 0 ${baseDistance.sm};
	min-height: 40px;
	width: 100%;
	border-radius: ${radius.sm};
	font-size: ${fontSize.lg};
	line-height: 24px;
	font-weight: ${fontWeight.normal};
	color: ${colorPalette.gray.c950};
	cursor: pointer;

	span {
		${ELLIPSIS}
	}

	&.active {
		background-color: ${colorPalette.primary.c200Light};
		color: ${colorPalette.primary.c500Primary};

		svg.info {
			display: block;
			min-width: ${baseDistance.lg};
			min-height: ${baseDistance.lg};
			padding: ${baseDistance.xs};
		}
	}

	svg {
		display: none;
		border: 1px solid transparent;
	}

	&:hover {
		background-color: ${colorPalette.gray.c200Light};
		color: ${colorPalette.gray.c950};

		svg.info {
			display: block;
			min-width: ${baseDistance.lg};
			min-height: ${baseDistance.lg};
			padding: ${baseDistance.xs};

			&:hover {
				border: 1px solid ${colorPalette.gray.c950};
				border-radius: ${radius.sm};
			}
		}

		&.active {
			background-color: ${colorPalette.primary.c200Light};
			color: ${colorPalette.primary.c500Primary};

			svg:hover {
				border-color: ${colorPalette.primary.c500Primary};
			}
		}
	}
`;
